import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Stack, FilePdf, ArrowRight, ArrowLeft, X, Check, Rocket } from "@phosphor-icons/react";
import { useI18n } from "@/i18n";

const ICONS = [ShieldCheck, Stack, FilePdf];
const ACCENTS = ["#38BDF8", "#0EA5E9", "#10B981"];

export function OnboardingWizard({ open, onClose, onStart }) {
  const { t } = useI18n();
  const STEPS = (t("md.obSteps") || []).map((s, i) => ({ ...s, Icon: ICONS[i], accent: ACCENTS[i] }));
  const [step, setStep] = useState(0);
  const [dontShow, setDontShow] = useState(false);
  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  const finish = (start) => {
    // Persist "seen" if the user finished the flow (Start) or explicitly opted out.
    onClose?.(dontShow || start);
    if (start) onStart?.();
    setStep(0);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="onboarding-wizard">
          <div className="absolute inset-0 bg-lc-navy/75 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            className="relative w-full max-w-md overflow-hidden rounded-sm border border-lc-ink/10 bg-white shadow-2xl"
          >
            <button onClick={() => finish(false)} data-testid="onboarding-close"
              className="absolute right-4 top-4 z-10 text-lc-steel hover:text-lc-ink transition-colors duration-200">
              <X size={18} />
            </button>

            {/* visual header */}
            <div className="relative flex h-40 items-center justify-center overflow-hidden bg-lc-navy">
              <div className="absolute inset-0 grain opacity-60" />
              <AnimatePresence mode="wait">
                <motion.div key={step}
                  initial={{ scale: 0.5, opacity: 0, rotate: -8 }} animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  exit={{ scale: 0.6, opacity: 0 }} transition={{ type: "spring", stiffness: 200, damping: 18 }}
                  className="relative flex h-20 w-20 items-center justify-center rounded-sm"
                  style={{ background: `${s.accent}1a`, border: `1px solid ${s.accent}55` }}>
                  <s.Icon size={40} weight="duotone" style={{ color: s.accent }} />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="p-7">
              {/* progress dots */}
              <div className="flex gap-1.5">
                {STEPS.map((_, i) => (
                  <div key={i} className="h-1 flex-1 rounded-full transition-colors duration-300"
                    style={{ background: i <= step ? s.accent : "#E2E8F0" }} />
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }}>
                  <h2 className="mt-5 font-serif text-2xl text-lc-ink" data-testid="onboarding-title">{s.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-lc-steel">{s.body}</p>
                </motion.div>
              </AnimatePresence>

              <label className="mt-6 flex cursor-pointer items-center gap-2 text-xs text-lc-steel">
                <button type="button" onClick={() => setDontShow((v) => !v)} data-testid="onboarding-dontshow"
                  className={`flex h-4 w-4 items-center justify-center rounded-sm border transition-colors duration-200 ${
                    dontShow ? "border-lc-ocean bg-lc-ocean text-white" : "border-lc-ink/25 bg-white"
                  }`}>
                  {dontShow && <Check size={11} weight="bold" />}
                </button>
                {t("md.obDontShow")}
              </label>

              <div className="mt-5 flex items-center justify-between gap-3">
                <button onClick={() => setStep((v) => Math.max(0, v - 1))} disabled={step === 0}
                  data-testid="onboarding-back"
                  className="inline-flex items-center gap-1.5 rounded-sm px-3 py-2.5 text-sm font-semibold text-lc-steel hover:text-lc-ink transition-colors duration-200 disabled:opacity-0">
                  <ArrowLeft size={15} weight="bold" /> {t("md.obBack")}
                </button>
                {last ? (
                  <button onClick={() => finish(true)} data-testid="onboarding-start"
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-sm bg-lc-navy px-5 py-3 text-sm font-semibold text-white hover:bg-lc-ocean transition-colors duration-200">
                    <Rocket size={16} weight="duotone" /> {t("md.obStart")}
                  </button>
                ) : (
                  <button onClick={() => setStep((v) => v + 1)} data-testid="onboarding-next"
                    className="inline-flex items-center gap-2 rounded-sm bg-lc-navy px-5 py-3 text-sm font-semibold text-white hover:bg-lc-ocean transition-colors duration-200">
                    {t("md.obNext")} <ArrowRight size={15} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
